<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .dialler-keypad-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-shrink: 0;
    position: relative;

    .dialler-keypad-input {
      align-items: center;
      display: flex;
      justify-content: center;
      @include colors.color(textWhite);
      @include mixins.body1;
      background: transparent;
      border: none;
      text-align: center;
      width: 100%;
      outline: none;
      position: relative;
      z-index: 2;

      &::placeholder {
        color: colors.get(interfaceTransparentWhite_90);
      }
    }

    .dialler-keypad-display {
      align-items: center;
      display: flex;
      justify-content: center;
      @include colors.color(textWhite);
      @include mixins.body1;
      min-height: 1.5rem;

      .placeholder {
        color: colors.get(interfaceTransparentWhite_90);
      }
    }

    .dialler-keypad {
      width: 100%;
      display: grid;
      gap: 8px;
      grid-template-columns: repeat(3, 1fr);
      margin-bottom: 8px;

      .dialler-keypad-button {
        align-items: center;
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(2rem);
        border-radius: 8px;
        min-height: 2.75rem;
        border: none;
        color: white;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        justify-content: center;
        user-select: none;

        &:not(.dialler-keypad-button-minimal):hover {
          background-color: rgba(0, 0, 0, 0.75);
        }

        &:focus {
          outline: 2px solid rgba(255, 255, 255, 0.5);
          outline-offset: 2px;
        }

        .key-main {
          @include mixins.h2();
          line-height: 1;
        }

        .key-secondary {
          @include mixins.body3;
          line-height: 1;
          min-height: 1em;
          letter-spacing: 1px;
          color: colors.get(interfaceTransparentWhite_70);

          &:empty {
            visibility: hidden;
          }
        }

        &.dialler-keypad-button-minimal {
          background-color: transparent;
          backdrop-filter: none;
        }
      }
    }
  }
</style>

<script>
  /**
   * Shared KeypadUI component for displaying a phone keypad
   * Used by both KeypadView (for sending DTMF tones) and KeypadDialView (for dialing)
   */

  let {
    value = $bindable(""),
    placeholder = "Enter digits",
    onDigitPress = () => {},
    showInput = false,
    oninput = undefined,
    children,
  } = $props();

  const keypadButtons = [
    { number: "1", secondary: " " },
    { number: "2", secondary: "ABC" },
    { number: "3", secondary: "DEF" },
    { number: "4", secondary: "GHI" },
    { number: "5", secondary: "JKL" },
    { number: "6", secondary: "MNO" },
    { number: "7", secondary: "PQRS" },
    { number: "8", secondary: "TUV" },
    { number: "9", secondary: "WXYZ" },
    { number: "#", secondary: "", minimal: true },
    { number: "0", secondary: "" },
    { number: "*", secondary: "", minimal: true },
  ];
</script>

<div class="dialler-keypad-container">
  <!-- Display entered digits - either as input or display -->
  {#if showInput}
    <input
      type="text"
      class="dialler-keypad-input"
      bind:value
      {placeholder}
      {oninput}
    />
  {:else}
    <div class="dialler-keypad-display">
      {#if value}
        {value}
      {:else}
        <span class="placeholder">{placeholder}</span>
      {/if}
    </div>
  {/if}

  <!-- Keypad buttons -->
  <div class="dialler-keypad">
    {#each keypadButtons as button}
      <button
        class="dialler-keypad-button {button.minimal
          ? 'dialler-keypad-button-minimal'
          : ''}"
        onclick={() => onDigitPress(button.number)}
        type="button"
        data-number={button.number}
      >
        <div class="key-main">{button.number}</div>
        {#if button.secondary && button.secondary !== ""}
          <div class="key-secondary">{button.secondary}</div>
        {/if}
      </button>
    {/each}
  </div>

  <!-- Slot for additional content (e.g., continue button, candidate list) -->
  {#if children}
    {@render children()}
  {/if}
</div>
